import { InfoNotice, InfoNoticeAction } from '@/components/patterns/info-notice'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { Can } from '@/lib/casl'
import { useCondolencesIntegration } from '@/lib/services/condolences/condolences.query'
import { AlertTriangle } from 'lucide-react'

type CondolencesReconnectNoticeProps = {
  className?: string
}

/**
 * Shown where shops look for orders when Condolences stopped accepting their saved connection: new Condolences
 * orders stop importing until someone signs in again on the Condolences settings page. Only people who can open
 * that page and reconnect there see the notice or load the connection.
 */
export function CondolencesReconnectNotice({ className }: CondolencesReconnectNoticeProps) {
  return (
    <Can do="list" on="business-settings">
      <Can do="list" on="condolences">
        <Can do="edit" on="condolences">
          <ReconnectNotice className={className} />
        </Can>
      </Can>
    </Can>
  )
}

function ReconnectNotice({ className }: CondolencesReconnectNoticeProps) {
  const path = usePathHelper()
  const orgId = useRouteOrganizationId()
  const { data: integration } = useCondolencesIntegration()
  const needsReconnect = Boolean(
    integration?.enabled && integration.has_token && !integration.connected
  )

  if (!needsReconnect) {
    return null
  }

  return (
    <InfoNotice
      title="Reconnect Condolences"
      tone="danger"
      icon={<AlertTriangle />}
      className={className}
      action={
        <InfoNoticeAction {...path.condolences.index().using({ orgId }).link}>
          Reconnect
        </InfoNoticeAction>
      }
    >
      Condolences stopped accepting our connection, so new Condolences orders aren’t coming in. Sign
      in with your Condolences email and password to start receiving them again.
    </InfoNotice>
  )
}
